import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  output,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { Subscription } from 'rxjs';
import { LoginService } from '../../auth/login.service';
import {
  markBirthdayCelebrationSeen,
  pickCelebrationMessageIndex,
  resolveCelebrationMessages,
} from './birthday-celebration.utils';

const MESSAGE_KEY = 'birthdayCelebration.messages';

@Component({
  selector: 'app-birthday-celebration',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslatePipe, MatButtonModule],
  templateUrl: './birthday-celebration.component.html',
  styleUrl: './birthday-celebration.component.scss',
})
export class BirthdayCelebrationComponent implements OnInit, OnDestroy {
  private readonly login = inject(LoginService);
  private readonly translate = inject(TranslateService);
  private langSub?: Subscription;
  /** Stable for this overlay open; re-resolve string on language change. */
  private messageIndex = 0;

  readonly dismissed = output<void>();
  readonly firstName = signal('');
  readonly message = signal('');
  /** Stable particle seeds for CSS confetti (no animation lib). */
  readonly pieces = Array.from({ length: 48 }, (_, i) => ({
    id: i,
    left: `${(i * 17 + 7) % 100}%`,
    delay: `${(i % 12) * 0.12}s`,
    duration: `${2.4 + (i % 5) * 0.35}s`,
    hue: (i * 47) % 360,
    size: 6 + (i % 5) * 2,
  }));

  ngOnInit(): void {
    const user = this.login.getUser();
    const name =
      user?.contact?.firstName?.trim() ||
      user?.firstName?.trim() ||
      this.translate.instant('birthdayCelebration.friend');
    this.firstName.set(name);

    const initial = resolveCelebrationMessages(this.translate.instant(MESSAGE_KEY));
    this.messageIndex = pickCelebrationMessageIndex(initial.length || 1);
    this.applyMessage();
    this.langSub = this.translate.onLangChange.subscribe(() => this.applyMessage());
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  private applyMessage(): void {
    const list = resolveCelebrationMessages(this.translate.instant(MESSAGE_KEY));
    if (!list.length) {
      this.message.set('');
      return;
    }
    this.message.set(list[this.messageIndex % list.length]!);
  }

  onDismiss(): void {
    const user = this.login.getUser();
    if (user?.id) {
      markBirthdayCelebrationSeen(user.id);
    }
    this.dismissed.emit();
  }
}
